import { Children, ReactElement, ReactNode, cloneElement, isValidElement, useState } from "react";
import {
    Box,
    IconButton,
    List,
    ListItem,
    Collapse,
    Typography,
    SxProps,
    Theme,
    useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import moment from "moment";
import CommentIcon from '@mui/icons-material/Comment';
import CommentsDisabledIcon from '@mui/icons-material/CommentsDisabled';

export interface Row {
    items: (string | Date | number)[];
    internalNodes?: Row[];
    leafNodes?: Row[];
    color?: string;
    modalData?: string[];
}

interface NestedListProps {
    columns?: string[];
    noDataText: string;
    hasModal?: boolean;
    onModalOpen?: (data: string[]) => void;
    level?: number;
    sx?: SxProps<Theme>;
}

const ListItemWithNodes = ({
    row,
    columns,
    noDataText,
    level = 1,
    hasModal,
    onModalOpen,
}: NestedListProps & {
    row: Row;
}) => {
    const [isListExpanded, setIsListExpanded] = useState(false);
    const theme = useTheme();

    return (
        <>
            <ListItem
                sx={{
                    paddingInline: { xs: `${level * 4}px`, sm: `${level * 24}px`, md: `${level * 40}px`, lg: `${level * 60}px` },
                }}
            >
                <Box display="flex" flexGrow={1} alignItems="center">
                    <IconButton size="small" onClick={() => setIsListExpanded(!isListExpanded)}>
                        {isListExpanded ? (
                            <KeyboardArrowUpIcon />
                        ) : (
                            <KeyboardArrowDownIcon />
                        )}
                    </IconButton>

                    <Box
                        display={"flex"}
                        justifyContent={"space-between"}
                        width={"100%"}
                    >
                        {row.items.map((item, index) => {
                            return (
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        color: row.color,
                                    }}
                                    key={index}
                                >
                                    {moment.isDate(item)
                                        ? moment.utc(item).local().format("LL")
                                        : item}
                                </Typography>
                            );
                        })}
                    </Box>
                </Box>
            </ListItem>
            {row.internalNodes && (
                <Collapse in={isListExpanded} timeout="auto" unmountOnExit>
                    <NestedList
                        rows={row.internalNodes}
                        columns={columns}
                        level={level + 1}
                        noDataText={noDataText}
                        onModalOpen={onModalOpen}
                        hasModal={true}
                    />
                </Collapse>
            )}
            {row.leafNodes && (
                <Collapse in={isListExpanded} timeout="auto" unmountOnExit>
                    <Box
                        display={"grid"}
                        gap={2}
                        sx={{
                            paddingInline: { xs: `${level * 6}px`, sm: `${level * 30}px`, md: `${level * 60}px`, lg: `${level * 80}px` },
                        }}
                    >
                        {columns && row.leafNodes.length > 0 && (
                            <Box
                                display={"grid"}
                                gridTemplateColumns={"1fr ".repeat(
                                    columns.length
                                )}
                                textAlign={"center"}
                            >
                                {columns.map((column, index) => {
                                    return (
                                        <Typography
                                            sx={{ fontWeight: "bold" }}
                                            key={index}
                                        >
                                            {column}
                                        </Typography>
                                    );
                                })}
                            </Box>
                        )}
                        {row.leafNodes.map((node, nodeIndex) => {
                            return (
                                <Box
                                    display={"grid"}
                                    gridTemplateColumns={"1fr ".repeat(
                                        node.items.length + (hasModal ? 1 : 0)
                                    )}
                                    textAlign={"center"}
                                    key={nodeIndex}
                                >
                                    {node.items.map((item, index) => {
                                        return (
                                            <Typography key={index}>
                                                {moment.isDate(item)
                                                    ? moment
                                                        .utc(item)
                                                        .local()
                                                        .format("LL")
                                                    : item}
                                            </Typography>
                                        );
                                    })}
                                    {hasModal && onModalOpen && (
                                        <Box>
                                            {node.modalData && node.modalData.length ? (
                                                <IconButton onClick={() => onModalOpen(node.modalData!)}>
                                                    <CommentIcon sx={{ color: theme.palette.primary.main }}></CommentIcon>
                                                </IconButton>
                                            ) : (
                                                <CommentsDisabledIcon></CommentsDisabledIcon>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}

                        {row.leafNodes.length === 0 && (
                            <Typography textAlign={"center"}>
                                {noDataText}
                            </Typography>
                        )}
                    </Box>
                </Collapse >
            )}
        </>
    );
};

const NestedList = ({
    rows,
    columns,
    noDataText,
    level = 1,
    sx,
    hasModal = false,
    onModalOpen,
}: NestedListProps & { rows: Row[] }) => {



    return (
        <List
            sx={{
                backgroundColor: (theme) => theme.palette.background.paper,
                padding: 0,
                ...sx,
            }}
        >
            {rows.map((row, index) => (
                <ListItemWithNodes
                    key={index}
                    row={row}
                    level={level}
                    columns={columns}
                    noDataText={noDataText}
                    hasModal={hasModal}
                    onModalOpen={onModalOpen}
                />
            ))}
        </List>
    );
};

export default NestedList;
